import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { IDate } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Defined } from '@avantstay/graphql-ts-client'

import { arriere } from '@new/domains/fastBookingsTapeChart/stores/arriere'

import { trackError } from '@tracking/customEventReport'

type HomeCheckInStatusView = Defined<
  Awaited<ReturnType<typeof arriere.fetchHomeCheckInStatus>>
>[number]

type HomeReadinessStatus = Defined<
  Defined<HomeCheckInStatusView['home']>['operationReadinessStatus']
>

class ObservableHomeReadinessStatus {
  cleanDetails?: HomeReadinessStatus['cleanDetails']
  othersCasesDetails?: HomeReadinessStatus['othersCasesDetails']
  inspectedDetails?: HomeReadinessStatus['inspectedDetails']
  status?: HomeReadinessStatus['status']
  statusUpdatedAt?: HomeReadinessStatus['statusUpdatedAt']

  constructor(initial: HomeReadinessStatus) {
    makeObservable(this, {
      cleanDetails: observable,
      othersCasesDetails: observable,
      inspectedDetails: observable,
      status: observable,
      statusUpdatedAt: observable,
      updateFrom: action.bound,
    })

    this.cleanDetails = initial.cleanDetails
    this.othersCasesDetails = initial.othersCasesDetails
    this.inspectedDetails = initial.inspectedDetails
    this.status = initial.status
    this.statusUpdatedAt = initial.statusUpdatedAt
  }

  updateFrom(newStatus: HomeReadinessStatus) {
    if (
      newStatus.cleanDetails != null &&
      newStatus.cleanDetails.status !== this.cleanDetails?.status
    ) {
      this.cleanDetails = newStatus.cleanDetails
    }

    if (
      newStatus.othersCasesDetails != null &&
      newStatus.othersCasesDetails !== this.othersCasesDetails
    ) {
      this.othersCasesDetails = newStatus.othersCasesDetails
    }

    if (
      newStatus.inspectedDetails != null &&
      newStatus.inspectedDetails.status !== this.inspectedDetails?.status
    ) {
      this.inspectedDetails = newStatus.inspectedDetails
    }

    if (newStatus.statusUpdatedAt != null && newStatus.statusUpdatedAt !== this.statusUpdatedAt) {
      this.statusUpdatedAt = newStatus.statusUpdatedAt
    }

    if (newStatus.status != null && newStatus.status !== this.status) {
      this.status = newStatus.status
    }
  }
}

const getLastestStatusTime = (homes: HomeCheckInStatusView[]) => {
  const dates = homes
    .map(home => home?.home?.operationReadinessStatus?.updatedAt)
    .filter((date): date is IDate => !!date)

  return (
    dates?.reduce((latest, current) => {
      return new Date(current) > new Date(latest) ? current : latest
    }, dates[0]) ?? new Date()
  )
}

class ArrivalModeColumnStore {
  homeStatuses = new Map<string, ObservableHomeReadinessStatus>()

  private lastUpdatedAtTime?: IDate

  constructor() {
    makeObservable<ArrivalModeColumnStore, 'lastUpdatedAtTime'>(this, {
      homeStatuses: observable,
      lastUpdatedAtTime: observable,
      updatedSince: computed,
      setHomeStatus: action.bound,
      fetchHomeStatuses: action.bound,
    })
  }

  get updatedSince() {
    return this.lastUpdatedAtTime
  }

  setHomeStatus(homeId: string, readinessStatus: HomeReadinessStatus) {
    const existing = this.homeStatuses.get(homeId)
    if (existing) {
      existing.updateFrom(readinessStatus)
    } else {
      this.homeStatuses.set(homeId, new ObservableHomeReadinessStatus(readinessStatus))
    }
  }

  async fetchHomeStatuses(homeIds?: string[], arrivalsMode?: boolean) {
    if (!homeIds?.length || !arrivalsMode) return

    const currentHomeIdsSet = new Set(this.homeStatuses.keys())

    const hasAllSameHomes = homeIds.every(id => currentHomeIdsSet.has(id))

    const updatedSince = hasAllSameHomes ? this.updatedSince : undefined

    try {
      const response = await arriere.fetchHomeCheckInStatus({
        homeIds,
        updatedSince,
      })

      this.lastUpdatedAtTime = getLastestStatusTime(response)

      runInAction(() => {
        response.forEach(item => {
          if (item.id && item.home?.operationReadinessStatus) {
            this.setHomeStatus(item.id, item.home.operationReadinessStatus)
          }
        })
      })
    } catch (error) {
      trackError(`Failed to fetch status list: ${error}`, { homeIds })
      throw error
    }
  }
}

export const arrivalModeColumnStore = new ArrivalModeColumnStore()
